import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Star } from 'lucide-react';
import style from './ClientCommentList.module.css';
import deleteCommentModal from '@/ui/assets/icons/deleteCommentModal.svg';
import { formatCommentDate, sortCommentsNewestFirst } from '@/utils/commentDates';
import { deleteCommentFromProduct } from '@services/adminDeleteCommentFromProductFirebaseService';

const renderCommentStars = (stars) => {
    const count = Math.min(Math.max(Number(stars) || 0, 0), 5);
    if (count === 0) return null;

    return (
        <div className={style.commentStars} aria-label={`${count} de 5 estrellas`}>
            {Array.from({ length: 5 }, (_, i) => (
                <Star
                    key={i}
                    size={14}
                    fill={i < count ? '#FFB84D' : 'none'}
                    color={i < count ? '#FFB84D' : '#D4C4BE'}
                    strokeWidth={1.5}
                />
            ))}
        </div>
    );
};

const ClientCommentList = ({
    productId,
    comments,
    onDeleteComment,
    onRestoreComment,
    deleteCommentIcon,
}) => {

    const [selectedComment, setSelectedComment] = useState(null);
    const [commentToDelete, setCommentToDelete] = useState(null);
    const [expandedComments, setExpandedComments] = useState({});

    const commentRefs = useRef({});

    const orderedComments = useMemo(
        () => sortCommentsNewestFirst(comments),
        [comments]
    );

    useEffect(() => {
        const updatedExpanded = {};
        Object.keys(commentRefs.current).forEach((key) => {
            const element = commentRefs.current[key];
            if (element) {
                updatedExpanded[key] = element.scrollHeight > element.clientHeight;
            }
        });
        setExpandedComments(updatedExpanded);
    }, [orderedComments]);

    if (!orderedComments.length) {
        return (
            <div className={style.noComments}>
                <p>No hay opiniones aún. ¡Sé el primero en calificar!</p>
            </div>
        );
    }

    return (
        <div className={style.detailContainer}>
            <div className={style.commentsList}>
                {orderedComments.map((item, index) => {
                    const commentKey = item.user_id || `comment-${index}`;

                    return (
                        <div key={commentKey} className={style.commentCard}>
                            <img
                                src={
                                    typeof item.avatar_url === 'object'
                                        ? item.avatar_url.path
                                        : item.avatar_url
                                }
                                alt="avatar"
                            />

                            <div className={style.commentContent}>
                                <div className={style.commentHeader}>
                                    <div className={style.commentHeaderMain}>
                                        <span className={style.userName}>
                                            {String(item.user_name)}
                                        </span>
                                        {renderCommentStars(item.stars_given)}
                                    </div>

                                    <div className={style.commentHeaderRight}>
                                        <span className={style.commentDate}>
                                            {formatCommentDate(item)}
                                        </span>

                                        {onDeleteComment && deleteCommentIcon && (
                                            <button
                                                type="button"
                                                className={style.deleteCommentBtn}
                                                onClick={() => setCommentToDelete(item)}
                                                title="Eliminar comentario"
                                            >
                                                {typeof deleteCommentIcon === 'string' ? (
                                                    <img src={deleteCommentIcon} alt="Eliminar comentario" />
                                                ) : (
                                                    deleteCommentIcon
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <p
                                    ref={(el) => {
                                        commentRefs.current[commentKey] = el;
                                    }}
                                    className={style.commentText}
                                >
                                    {item.comment}
                                </p>

                                {expandedComments[commentKey] && (
                                    <button
                                        className={style.readMoreBtn}
                                        onClick={() => setSelectedComment(item)}
                                    >
                                        Ver más
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Modal de Lectura Completa */}
            {selectedComment && (
                <div className={style.modalOverlay} onClick={() => setSelectedComment(null)}>
                    <div className={style.commentModal} onClick={(e) => e.stopPropagation()}>
                        <button className={style.closeModalBtn} onClick={() => setSelectedComment(null)}>✕</button>
                        <div className={style.modalTop}>
                            <img
                                src={
                                    typeof selectedComment.avatar_url === 'object'
                                        ? selectedComment.avatar_url.path
                                        : selectedComment.avatar_url
                                }
                                alt="avatar"
                                className={style.modalAvatar}
                            />
                            <div className={style.modalHeader}>
                                <span className={style.modalUserName}>{selectedComment.user_name}</span>
                                {renderCommentStars(selectedComment.stars_given)}
                                <span className={style.modalDate}>{formatCommentDate(selectedComment)}</span>
                            </div>
                        </div>
                        <p className={style.fullComment}>{selectedComment.comment}</p>
                    </div>
                </div>
            )}

            {/* Modal de Confirmación de Eliminación */}
            {commentToDelete && (
                <div className={style.deleteModalOverlay} onClick={() => setCommentToDelete(null)}>
                    <div className={style.deleteModal} onClick={(e) => e.stopPropagation()}>
                        <div className={style.deleteIconContainer}>
                            <img src={deleteCommentModal} alt="Eliminar comentario" className={style.deleteIcon} />
                        </div>

                        <h2 className={style.deleteModalTitle}>Eliminar comentario</h2>
                        <p className={style.deleteModalText}>¿Estás seguro de que deseas eliminar este comentario?</p>
                        <div className={style.deleteDivider}></div>

                        <div className={style.deleteModalButtons}>
                            <button className={style.cancelBtn} onClick={() => setCommentToDelete(null)}>
                                Cancelar
                            </button>

                            <button
                                className={style.confirmDeleteBtn}
                                onClick={async () => {
                                    const deletedComment = commentToDelete;
                                    const targetProductId =
                                        productId ||
                                        localStorage.getItem('admin_selected_product_id') ||
                                        localStorage.getItem('client_selected_product_id');
                                    const userId = deletedComment.user_id;

                                    try {
                                        if (!targetProductId) {
                                            throw new Error("No se detectó un productId válido.");
                                        }

                                        if (onDeleteComment) {
                                            onDeleteComment(deletedComment);
                                        }

                                        setCommentToDelete(null);

                                        await deleteCommentFromProduct(targetProductId, userId);
                                    } catch (error) {
                                        console.error("Error al procesar la baja del comentario:", error);

                                        if (onRestoreComment) {
                                            onRestoreComment(deletedComment);
                                        }

                                        alert("No se pudo eliminar el comentario. Inténtalo de nuevo.");
                                    }
                                }}
                            >
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
};

export default ClientCommentList;